import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { usePageMeta } from "@/hooks/use-page-meta";

export default function PrivacyPolicy() {
  usePageMeta({
    title: "Privacy Policy - Skiline Recruitment",
    description: "Read Skiline Recruitment's Privacy Policy to understand how we collect, use, and protect your personal information.",
    keywords: "privacy policy, Skiline Recruitment, data protection, personal information",
  });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navigation />

      {/* Hero Section */}
      <section className="bg-accent py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center space-y-8">
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-foreground">
            Privacy Policy
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
            Last updated: March 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-4xl mx-auto px-6 md:px-8 space-y-12">
          <p className="text-lg text-muted-foreground leading-relaxed">
            Skiline Recruitment ("we", "us", "our") is a recruitment service based in Chennai, India.
            We help housewives and retired professionals find suitable employment opportunities.
            This Privacy Policy explains what information we collect, how we use it, and how we protect it.
          </p>

          {/* Section 1 */}
          <div className="space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground">
              1. Information We Collect
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              When you fill out our lead form on Facebook or Instagram, or contact us directly, we collect:
            </p>
            <ul className="list-disc list-inside text-lg text-muted-foreground leading-relaxed space-y-2 ml-4">
              <li>Your full name</li>
              <li>Your phone number</li>
              <li>Your email address</li>
            </ul>
          </div>

          {/* Section 2 */}
          <div className="space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground">
              2. How We Use Your Information
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We use the information you provide only to:
            </p>
            <ul className="list-disc list-inside text-lg text-muted-foreground leading-relaxed space-y-2 ml-4">
              <li>Contact you about suitable job opportunities in Chennai</li>
              <li>Evaluate your profile for suitable roles within our organisation</li>
              <li>Follow up with you about your application</li>
            </ul>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We do not use your information for any other purpose without your consent.
            </p>
          </div>

          {/* Section 3 */}
          <div className="space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground">
              3. We Do Not Sell Your Information
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We do not sell, rent, or share your personal information with any third party.
              Your details are used solely by the Skiline Recruitment team to assess your suitability
              for roles within our organisation.
            </p>
          </div>

          {/* Section 4 */}
          <div className="space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground">
              4. How We Store Your Information
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Your information is stored securely and is only accessible to the Skiline Recruitment team.
              We keep your details for as long as necessary to assist with your job search.
              If you would like us to delete your information at any time, simply contact us and we will do so promptly.
            </p>
          </div>

          {/* Section 5 */}
          <div className="space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground">
              5. Meta (Facebook and Instagram) Ads
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We run ads on Facebook and Instagram to reach people who may be interested in our services.
              If you submit a form through one of our ads, your information is collected through Meta's
              lead form system and shared with us. Meta's own Privacy Policy also applies to data collected
              on their platform.
            </p>
          </div>

          {/* Section 6 */}
          <div className="space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground">
              6. Your Rights
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              You have the right to:
            </p>
            <ul className="list-disc list-inside text-lg text-muted-foreground leading-relaxed space-y-2 ml-4">
              <li>Ask us what information we hold about you</li>
              <li>Ask us to correct any wrong information</li>
              <li>Ask us to delete your information at any time</li>
              <li>Withdraw your consent and stop receiving communications from us</li>
            </ul>
            <p className="text-lg text-muted-foreground leading-relaxed">
              To exercise any of these rights, simply contact us using the details below.
            </p>
          </div>

          {/* Section 7 */}
          <div className="space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground">
              7. Cookies
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Our website may use basic cookies to improve your browsing experience.
              We do not use cookies to track you across other websites or for advertising purposes.
            </p>
          </div>

          {/* Section 8 */}
          <div className="space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground">
              8. Changes to This Policy
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. Any changes will be posted on this
              page with a new date. We encourage you to check this page occasionally.
            </p>
          </div>

          {/* Section 9 */}
          <div className="space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground">
              9. Contact Us
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <div className="bg-accent rounded-lg p-6 space-y-2">
              <p className="text-lg font-semibold text-foreground">Skiline Recruitment</p>
              <p className="text-lg text-muted-foreground">
                Email:{" "}
                <a
                  href="mailto:skilinerecruitment360@gmail.com"
                  className="text-primary hover:underline"
                >
                  skilinerecruitment360@gmail.com
                </a>
              </p>
              <p className="text-lg text-muted-foreground">
                Website:{" "}
                <a
                  href="https://www.skilinerecruitment360.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  www.skilinerecruitment360.in
                </a>
              </p>
              <p className="text-lg text-muted-foreground">Location: Chennai, Tamil Nadu, India</p>
            </div>
          </div>

          <p className="text-lg text-muted-foreground leading-relaxed italic border-t border-border pt-8">
            This Privacy Policy is effective as of March 2026 and applies to all information collected
            through our website, social media ads, and direct communications.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
